import type { ReactNode } from 'react'

// ---------------------------------------------------------------------------
// Curated inline-SVG icon set for appointment types. Keyed by name; the value
// stored in `profile.icon` is either one of these keys OR a custom image URL.
// All icons draw with `currentColor` so they inherit the surrounding text color.
// When adding icons, update the icon key list in CONFIG.md.
// ---------------------------------------------------------------------------

const PATHS: Record<string, ReactNode> = {
  stethoscope: (
    <>
      <path d="M5 3v5a4 4 0 0 0 8 0V3" />
      <path d="M9 16v1a5 5 0 0 0 10 0v-2" />
      <circle cx="19" cy="11" r="2.2" />
    </>
  ),
  pill: (
    <>
      <rect x="3.5" y="8.5" width="17" height="7" rx="3.5" transform="rotate(-45 12 12)" />
      <path d="M9 9l6 6" />
    </>
  ),
  // Half-filled capsule — the solid half keeps it reading as a pill, not a clip.
  capsule: (
    <>
      <rect x="4" y="9" width="16" height="6" rx="3" />
      <path d="M12 9H7a3 3 0 0 0 0 6h5Z" fill="currentColor" stroke="none" />
    </>
  ),
  brain: (
    <>
      <path d="M9 4a2.5 2.5 0 0 0-2.5 2.5A2.5 2.5 0 0 0 5 11a2.5 2.5 0 0 0 1.5 4.5A2.5 2.5 0 0 0 9 18.5a2 2 0 0 0 3-1.7V5.7A2 2 0 0 0 9 4Z" />
      <path d="M15 4a2.5 2.5 0 0 1 2.5 2.5A2.5 2.5 0 0 1 19 11a2.5 2.5 0 0 1-1.5 4.5A2.5 2.5 0 0 1 15 18.5a2 2 0 0 1-3-1.7" />
    </>
  ),
  heart: <path d="M12 20s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.4-7 10-7 10Z" />,
  heartbeat: (
    <>
      <path d="M3 12h4l2-4 3 8 2-5 1.5 1H21" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2.5" />
      <path d="M4 9h16M9 3v4M15 3v4" />
    </>
  ),
  clipboard: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2.5" />
      <path d="M9 4h6v3H9z" />
      <path d="M8 12h8M8 16h6" />
    </>
  ),
  syringe: (
    <>
      <path d="M14 4l6 6M16 6l-9 9-3 4 4-3 9-9" />
      <path d="M12 8l4 4" />
    </>
  ),
  // Bathroom scale: rounded square with a gauge arc + needle (weight loss).
  scale: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2.5" />
      <path d="M8 10a4 4 0 0 1 8 0" />
      <path d="M12 10l1.7-2.3" />
    </>
  ),
  // Tissue box with a tissue pulled up through the slot (allergy).
  tissues: (
    <>
      <rect x="3.5" y="11.5" width="17" height="8" rx="1.5" />
      <path d="M9 11.5c-.2-2.6.8-5.5 3-5.8 2.2.3 3.2 3.2 3 5.8" />
      <path d="M12 8v3.5" />
    </>
  ),
  lungs: (
    <>
      <path d="M12 3v8" />
      <path d="M9 8c0 4-1 5-3 8-1.5 2-4 1.5-4-1 0-4 2-7 4-8 2-1 3 0 3 1Z" />
      <path d="M15 8c0 4 1 5 3 8 1.5 2 4 1.5 4-1 0-4-2-7-4-8-2-1-3 0-3 1Z" />
    </>
  ),
  droplet: <path d="M12 3s6 6.5 6 10.5A6 6 0 0 1 6 13.5C6 9.5 12 3 12 3Z" />,
  shield: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 7.7-7 10-4-2.3-7-5.5-7-10V6Z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </>
  ),
  doctor: (
    <>
      <circle cx="12" cy="7" r="3.2" />
      <path d="M5 21v-1a7 7 0 0 1 14 0v1" />
    </>
  ),
  thermometer: (
    <>
      <path d="M14 14V5a2 2 0 0 0-4 0v9a4 4 0 1 0 4 0Z" />
      <path d="M12 14V8" />
    </>
  ),
  microscope: (
    <>
      <path d="M6 21h12" />
      <path d="M9 21a6 6 0 0 0 7-9" />
      <path d="M11 5l3 3-2 2-3-3z" />
      <path d="M8 8l2 2" />
    </>
  ),
  bandage: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="4" transform="rotate(-45 12 12)" />
      <path d="M10 10h.01M14 14h.01M12 12h.01M10 14h.01M14 10h.01" />
    </>
  ),
  tooth: <path d="M12 4c-2-1.5-5-1.5-6 .5-1.2 2.4 0 5 .5 8 .3 1.8 0 5 1.5 5s1-3 2-3 .5 3 2 3 1.2-3.2 1.5-5c.5-3 1.7-5.6.5-8-1-2-4-2-6-.5Z" />,
  chat: (
    <>
      <path d="M4 5h16v11H9l-4 4V5Z" />
      <path d="M8 9h8M8 12h5" />
    </>
  ),
  activity: <path d="M3 12h4l2 7 4-16 2 9h6" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  flask: (
    <>
      <path d="M9 3h6M10 3v6l-4.5 8A2 2 0 0 0 7.3 20h9.4a2 2 0 0 0 1.8-3L14 9V3" />
      <path d="M8 15h8" />
    </>
  ),
  // "Locate me" crosshair/target (flaticon 565949 equivalent) — used in the zip bar.
  target: (
    <>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  )
}

export function isIconUrl(value?: string): boolean {
  return !!value && (/^https?:\/\//.test(value) || value.startsWith('/') || value.startsWith('data:'))
}

/**
 * Render an appointment-type icon. `value` is a curated key, a URL, or undefined.
 * Legacy Font-Awesome-style hints ("fa-...") fall back to a sensible default.
 */
export function Icon({
  value,
  size = 24,
  className
}: {
  value?: string
  size?: number
  className?: string
}) {
  if (isIconUrl(value)) {
    return <img src={value} alt="" width={size} height={size} className={className} style={{ objectFit: 'contain' }} />
  }
  const key = value && PATHS[value] ? value : 'stethoscope'
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {PATHS[key]}
    </svg>
  )
}
